import { useState } from 'react'
import { InfiniteData, QueryClient, useQueryClient } from '@tanstack/react-query'
import { useDebouncedValue } from '@mantine/hooks'
import { PaginatedResponse } from '@pubstack/common/src/pagination'
import { Site } from '@pubstack/common/src/site'
import { byName } from '@pubstack/common/src/sort'
import { SITES_QUERY_KEYS, useInfiniteSites } from '~/api/app/sites.api'

export const MEDIA_PAGE_SIZE = 50

const MIN_LOCAL_MATCHES = 20
const SEARCH_DEBOUNCE_MS = 300

const MEDIA_LIST_PARAMS = { limit: MEDIA_PAGE_SIZE, sortBy: 'name', sortOrder: 'ascending' } as const

export type KnownSite = Pick<Site, 'id' | 'name'>

type MediaListKeyParams = { filters: { search: string | null } }

const matchesSearch = (site: KnownSite, search: string) => site.name.toLowerCase().includes(search.toLowerCase())
const isLastPage = (page?: PaginatedResponse<Site>) => !!page && page.pagination.page >= page.pagination.totalPages

/**
 * Every media the picker's lists have brought back so far, and the searches the server answered down to their last page.
 * Tanstack's cache is the memory the pool lives in — the pages stay there once fetched, so the pool outlives the flyout:
 * reopening it, or typing a search a second time, asks the server nothing.
 */
const readMediaLists = (queryClient: QueryClient, scopeId: string) => {
  const knownSites: Record<string, KnownSite> = {}
  const exhaustedSearches: string[] = []
  for (const [key, data] of queryClient.getQueriesData<InfiniteData<PaginatedResponse<Site>>>({ queryKey: [...SITES_QUERY_KEYS(scopeId), 'infinite', MEDIA_LIST_PARAMS] })) {
    const pages = data?.pages ?? []
    for (const site of pages.flatMap((page) => page.data)) knownSites[site.id] = { id: site.id, name: site.name }
    // the server ran out of matches: the pool holds every media of this search, and with them every media of any search extending it
    const { filters } = key[3] as MediaListKeyParams
    if (Object.keys(filters).length === 1 && isLastPage(pages.at(-1))) exhaustedSearches.push(filters.search ?? '')
  }
  return { knownSites, exhaustedSearches }
}

/**
 * The medias offered by the ad quality rule picker: the scope's first page, then a search answered first from every
 * media seen so far and only then from the server, each list growing a page at a time as it is scrolled.
 */
export const useMediaSearch = (scopeId: string, rawSearch: string) => {
  const search = rawSearch.trim()
  const [debouncedSearch] = useDebouncedValue(search, SEARCH_DEBOUNCE_MS)
  const hasTypingSettled = search === debouncedSearch
  // The search the user scrolled to the end of while the pool alone was shown, so the server is asked even with enough local matches
  const [forcedSearch, setForcedSearch] = useState<string | null>(null)

  const queryClient = useQueryClient()
  const allSites = useInfiniteSites({ scopeId, filters: { search: null }, ...MEDIA_LIST_PARAMS })

  // Read on every render rather than memoised: the pages a query brings back land in the cache before the render they trigger
  const { knownSites, exhaustedSearches } = readMediaLists(queryClient, scopeId)
  const locallyMatchingSites = Object.values(knownSites).filter((site) => matchesSearch(site, search))
  const isSearchExhausted = exhaustedSearches.some((exhausted) => search.toLowerCase().includes(exhausted.toLowerCase()))
  const needsServer = !!search && hasTypingSettled && !isSearchExhausted && (locallyMatchingSites.length < MIN_LOCAL_MATCHES || forcedSearch === search)

  const searchedSites = useInfiniteSites({ scopeId, filters: { search: debouncedSearch }, ...MEDIA_LIST_PARAMS, enabled: needsServer })
  const searchedSitesPages = hasTypingSettled ? searchedSites.data.pages : []

  // `byName` is the order the server sorts its pages in, so a searched list that merges them with the pool reads as one list
  const sites: KnownSite[] = search
    ? [...new Map([...locallyMatchingSites, ...searchedSitesPages.flatMap((page) => page.data)].map((site) => [site.id, { id: site.id, name: site.name }])).values()].sort(byName)
    : allSites.data.pages.flatMap((page) => page.data)

  const hasSearchedPages = searchedSitesPages.length > 0
  const hasMore = search ? !isSearchExhausted && (!hasSearchedPages || searchedSites.hasNextPage) : allSites.hasNextPage

  const isSearching = !!search && !isSearchExhausted && locallyMatchingSites.length < MIN_LOCAL_MATCHES && (!hasTypingSettled || searchedSites.isLoading)
  const isLoadingMore = search ? searchedSites.isFetchingNextPage || (forcedSearch === search && searchedSites.isLoading) : allSites.isFetchingNextPage

  const loadMore = () => {
    if (!hasMore || isLoadingMore || isSearching) return
    if (!search) {
      allSites.fetchNextPage()
    } else if (hasSearchedPages) {
      searchedSites.fetchNextPage()
    } else {
      setForcedSearch(search)
    }
  }

  return { sites, knownSites, isLoading: allSites.isLoading, isSearching, isLoadingMore, loadMore }
}
